import { DoctorType, PatchPatientDto, QueryFactory } from '@lmt/shared-types';

import {
  CustomDictionaryName,
  useDictionaryByTitle,
  useDictionaryValuesById,
  useDictionaryValuesByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';

import { Button } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';

import globalStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import {
  FieldStatus,
  getPatientDict,
  getPatientFields,
  getUpdatedPatient,
  isEmpty,
  PatientData,
  titleFieldKey,
} from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/common';
import { MergePatientsFieldsTable } from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/MergePatientsFieldsTable';
import { MergePatientsResult } from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/MergePatientsResult';
import { PatientDataHeader } from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/PatientDataHeader';
import { getObjectHash } from '@lmt/unicorn-shared/app/components/merge-assistant/form/utils/hash-utils';
import { getFormattedDate } from '@lmt/unicorn-shared/app/components/patients/common';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { localizeSexType } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import i18next from 'i18next';
import { CardData } from '@lmt/unicorn-shared/app/components/card-reader/components/CardReaderHandler';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import styles from './components/MergePair.module.scss';
import { ToastIcon } from '../../common/icons/toast/ToastIcon';

type Props = {
  patient: PatientData;
  cardData: CardData;
};

export const MergePatientWithCardData: FC<Props & FormComponentProps> = (
  props,
) => {
  const queryClient = useQueryClient();
  const tabs = useTabs();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const i18nMerge = useScopedTranslation(
    'merge_assistant.merge_with_card_data',
  );

  const { patient, cardData, formType, formId, tryCompleteForm } = props;
  const [patientFields, setPatientFields] = useState<{
    [key: string]: FieldStatus;
  }>({});

  const doctorsQuery = QueryFactory.DoctorQuery.useGetDoctorsQuery({
    all: true,
    doctorType: DoctorType.External,
  });

  const insuranceCompanyDictionary = useDictionaryByTitle(
    CustomDictionaryName.InsuranceCompanies,
  );
  const insuranceCompaniesQuery = useDictionaryValuesById(
    insuranceCompanyDictionary?.id,
  );
  const titleDictQuery = useDictionaryValuesByTitle(
    CustomDictionaryName.PersonTitles,
  );

  const patientDict = useMemo(() => {
    const patientData = getPatientDict(patient);
    // include only patient fields that might be in card
    const cardDataKeys = Object.keys(cardData);
    return Object.fromEntries(
      Object.entries(patientData).filter(([key]) => cardDataKeys.includes(key)),
    );
  }, [patient, cardData]);

  const cardDict = useMemo(() => {
    return getPatientDict(cardData);
  }, [cardData]);

  const changeFieldStatus = useCallback((key: string, value: FieldStatus) => {
    setPatientFields((prevValues) => {
      return {
        ...prevValues,
        [key]: value,
      };
    });
  }, []);

  useEffect(() => {
    setPatientFields(getPatientFields(patientDict, cardDict));
  }, [patientDict, cardDict]);

  function getValue(fieldName: string, patientDict: { [key: string]: any }) {
    const value = patientDict[fieldName];
    if (isEmpty(value)) {
      return undefined;
    }
    if (fieldName === 'dateOfBirth' || fieldName === 'dateOfDeath') {
      return getFormattedDate(value);
    } else if (fieldName === 'sex') {
      return localizeSexType(value);
    } else if (fieldName === 'healthInsuranceCompanyId') {
      const company = (insuranceCompaniesQuery.data ?? []).find(
        (company) => company.id === value,
      );
      return company?.value;
    } else if (fieldName === 'title') {
      if (!Number.isNaN(Number(value))) {
        const title = (titleDictQuery.data?.data ?? []).find(
          (title) => title.id === value,
        );
        return i18next.t(title?.value as any, { ns: 'data-block-fields' });
      }
    } else if (fieldName === 'doctorId') {
      const doctor = (doctorsQuery.data?.data ?? []).find(
        (doctor) => doctor.id === value,
      );
      return doctor?.name;
    }
    return value.toString();
  }

  const selectAll = useCallback(
    (status: FieldStatus) => {
      for (const field in patientFields) {
        if (
          patientFields[field] !== FieldStatus.const &&
          ((!isEmpty(patientDict[field]) && !isEmpty(cardDict[field])) ||
            (status === FieldStatus.firstPatient &&
              !isEmpty(patientDict[field])) ||
            (status === FieldStatus.secondPatient && !isEmpty(cardDict[field])))
        ) {
          changeFieldStatus(field, status);
        }
      }
    },
    [patientFields, patientDict, cardDict],
  );

  const getPatientOrCardValue = useCallback(
    (fieldName: string) => {
      const fieldStatus = patientFields[fieldName];
      if (fieldStatus && fieldStatus !== FieldStatus.const) {
        return fieldStatus === FieldStatus.firstPatient
          ? patientDict[fieldName]
          : fieldStatus === FieldStatus.secondPatient
            ? cardDict[fieldName]
            : undefined;
      }
      return undefined;
    },
    [patientFields, patientDict, cardDict],
  );

  const updatePatient = useCallback(async () => {
    try {
      await tryCompleteForm(
        formType,
        formId,
        async () => {
          const patchDto: PatchPatientDto = getUpdatedPatient({
            fields: patientFields,
            customFields: {},
            patient1Dict: patientDict,
            patient2Dict: cardDict,
            patient1CustomFields: {},
            patient2CustomFields: {},
          });
          // set title as custom field
          const title = getPatientOrCardValue('title');
          if (title !== undefined) {
            const customValues = patient.values; // we need to set all custom values to prevent losing it
            if (title === null) {
              delete customValues[titleFieldKey];
            } else {
              customValues[titleFieldKey] = title;
            }
            patchDto.values = customValues;
          }
          if (patchDto.healthInsuranceCompanyId || patchDto.insuranceNumber) {
            patchDto.isSelfPayer = false;
          }
          const updatedPatient = await QueryFactory.PatientClient.editPatient(
            patient.id,
            patchDto,
          );
          queryClient.setQueryData(
            QueryFactory.PatientQuery.getPatientQueryKey(patient.id),
            updatedPatient,
          );

          // save card data hash to prevent opening this form again with the same data
          // !! we save card hash only if insurance number is set
          if (updatedPatient.insuranceNumber) {
            await QueryFactory.PatientCardHashClient.create({
              patientId: patient.id,
              cardHash: getObjectHash(cardData),
            });
          }

          tabs.openTab(
            pages.patientProfile.link({
              patientId: patient.id,
            }),
          );
          toast(i18nMerge.t('merge_successful_toast'), {
            icon: () => <ToastIcon type="success" />,
          });
        },
        i18nMerge.t('saving'),
      );
    } catch (err) {
      await modals.showError({ text: parseError(err) });
    }
  }, [
    formType,
    formId,
    tryCompleteForm,
    patientFields,
    patientDict,
    patient,
    cardDict,
    dispatch,
  ]);

  return (
    <div
      className={clsx(
        globalStyles.autoSizedContainer,
        globalStyles.autoSizedContainerWithScroll,
      )}
    >
      <div className={styles.dataContainer}>
        <div className={styles.rootGrid}>
          <PatientDataHeader patient={patient} cardData={cardData} />
          <MergePatientsFieldsTable
            fields={patientFields}
            changeField={changeFieldStatus}
            customFields={{}}
            changeCustomField={undefined}
            getFirstPatientValue={(fieldName) =>
              getValue(fieldName, patientDict)
            }
            getSecondPatientValue={(fieldName) => getValue(fieldName, cardDict)}
            getFirstPatientCustomValue={undefined}
            getSecondPatientCustomValue={undefined}
            selectAll={selectAll}
          />
        </div>
        <MergePatientsResult
          fields={patientFields}
          customFields={{}}
          getFirstPatientValue={(fieldName) => getValue(fieldName, patientDict)}
          getSecondPatientValue={(fieldName) => getValue(fieldName, cardDict)}
          getFirstPatientCustomValue={undefined}
          getSecondPatientCustomValue={undefined}
        />
      </div>
      <div
        className={clsx(
          globalStyles.submitContainer,
          styles.controlsContainerReverse,
        )}
      >
        <div className={styles.controls}>
          <Button
            disabled={Object.values(patientFields).includes(
              FieldStatus.notSelected,
            )}
            className={globalStyles.submit}
            title={i18nMerge.t('confirm_button')}
            onClick={updatePatient}
          />
        </div>
      </div>
    </div>
  );
};
